import * as api from '../../api';

import { useMemo, useState } from 'react';
import { Selector } from './Selector';
import { ResultCard } from './ResultCard';
import { calculateDistanceBetweenCoordinates } from '../ScheduleAppointment/AppointmentDisplay/sorting';
import { PharmacyWithDistance } from './types';
import { useBrowserCoordinates } from '../../hooks/common/useBrowserCoordinates';

import styles from './index.module.css';

interface Props {
  pharmacies: api.Pharmacy.PrevisitPharmacy[];
  patientPharmacies: api.Pharmacy.PrevisitPharmacy[];
  initPharmacy?: api.Pharmacy.PrevisitPharmacy;
  pending?: boolean;
  onChange?: (newPharmacy?: api.Pharmacy.PrevisitPharmacy) => void;
  fallbackCoordinates: {
    longitude: number | null;
    latitude: number | null;
  };
}

export const PharmacyInput = ({
  pharmacies,
  patientPharmacies,
  fallbackCoordinates,
  initPharmacy,
  pending,
  onChange,
}: Props) => {
  const [selectedPharmacyId, setSelectedPharmacyId] = useState<
    string | undefined | null
  >();
  const selectedPharmacy = pharmacies.find(
    (pharmacy) => pharmacy.id === selectedPharmacyId,
  );
  const displayPharmacy =
    selectedPharmacyId === undefined ? initPharmacy : selectedPharmacy;
  const browserCoordinates = useBrowserCoordinates();
  const currentCoordinates = browserCoordinates || fallbackCoordinates;
  const { longitude: uLongitude, latitude: uLatitude } = currentCoordinates;

  const pharmaciesWithDistance: PharmacyWithDistance[] = useMemo(() => {
    if (uLongitude === null || uLatitude === null) return pharmacies;
    return pharmacies.map((pharmacy) => {
      const { longitude, latitude } = pharmacy.address;
      if (!longitude || !latitude) return pharmacy;
      const distance = calculateDistanceBetweenCoordinates(
        { longitude, latitude },
        { longitude: uLongitude, latitude: uLatitude },
      );
      return { ...pharmacy, distance };
    });
  }, [uLongitude, uLatitude, pharmacies]);

  const onSelectedPharmacyChange = (newId: string | null) => {
    setSelectedPharmacyId(newId);
    if (!onChange) return;
    const newPharmacy = pharmacies.find((pharmacy) => pharmacy.id === newId);
    onChange(newPharmacy);
  };

  return (
    <div className={styles.Dropdown}>
      {displayPharmacy ? (
        <ResultCard
          name={displayPharmacy.name}
          address={displayPharmacy.address}
          onRemove={() => onSelectedPharmacyChange(null)}
        />
      ) : (
        <Selector
          sortedPharmacies={pharmaciesWithDistance}
          patientPharmacies={patientPharmacies}
          setSelectedPharmacyId={onSelectedPharmacyChange}
          pending={pending}
        />
      )}
    </div>
  );
};
